// Alert-rule evaluation. Pure functions — no React, no network. Called from
// the AlertsPanel against the latest snapshot the page has in memory, and
// re-evaluated on every realtime insert.

import type { AlertKind, AlertRule, AccountSnapshot } from '@/types/veritas';

export type AlertEvaluation = {
  breached: boolean;
  message: string;       // human-readable status line
  observed: number;      // the value being compared against threshold
};

const ALERT_KIND_LABELS: Record<AlertKind, string> = {
  drawdown_pct:    'Drawdown',
  daily_loss:      'Daily P&L floor',
  offline_minutes: 'Account offline',
};

export function alertKindLabel(k: AlertKind) {
  return ALERT_KIND_LABELS[k];
}

export function alertKindUnit(k: AlertKind) {
  switch (k) {
    case 'drawdown_pct':    return '%';
    case 'daily_loss':      return '';     // currency, formatted by caller
    case 'offline_minutes': return 'min';
  }
}

/** Describe a rule's threshold in a way that's unambiguous in UI labels. */
export function alertKindDirection(k: AlertKind): 'above' | 'below' {
  switch (k) {
    case 'drawdown_pct':    return 'above';   // "fire if dd > X"
    case 'daily_loss':      return 'below';   // "fire if day_pnl < -X"
    case 'offline_minutes': return 'above';   // "fire if offline > X min"
  }
}

/**
 * Evaluate a single rule against the latest snapshot. Returns:
 *   - breached:   true if the rule's condition is currently true
 *   - message:    short human description suitable for a status line
 *   - observed:   the raw measurement (for display + tooltips)
 *
 * Rules:
 *   drawdown_pct      → breached when snap.dd_pct          >  threshold
 *   daily_loss        → breached when snap.day_pnl         <  threshold (threshold is negative)
 *   offline_minutes   → breached when minutes since received_at > threshold
 *
 * Returns { breached: false, ... } for disabled rules or when the relevant
 * snapshot field is null.
 */
export function evaluateAlertRule(
  rule: AlertRule,
  snap: AccountSnapshot | undefined,
  // Trading-only Today P&L (deposits/withdrawals stripped) from
  // veritas.account_today_pnl. When provided, daily_loss compares against this
  // instead of the EA-supplied snap.day_pnl, so a deposit can't mask a real
  // intraday loss and a withdrawal can't falsely trip the floor. Falls back to
  // snap.day_pnl when undefined to stay backward-compatible.
  dayPnlTrading?: number | null,
): AlertEvaluation {
  if (!rule.enabled) {
    return { breached: false, message: 'Disabled', observed: 0 };
  }
  if (!snap) {
    return { breached: false, message: 'No data yet', observed: 0 };
  }

  switch (rule.kind) {
    case 'drawdown_pct': {
      const dd = snap.dd_pct ?? 0;
      const breached = dd > rule.threshold;
      return {
        breached,
        observed: dd,
        message: breached
          ? `${dd.toFixed(2)}% drawdown · over ${rule.threshold}% threshold`
          : `${dd.toFixed(2)}% drawdown · within ${rule.threshold}% limit`,
      };
    }

    case 'daily_loss': {
      const dpnl = dayPnlTrading ?? snap.day_pnl ?? 0;
      const breached = dpnl < rule.threshold;
      return {
        breached,
        observed: dpnl,
        message: breached
          ? `Today ${dpnl.toFixed(2)} · under ${rule.threshold} floor`
          : `Today ${dpnl.toFixed(2)} · above ${rule.threshold} floor`,
      };
    }

    case 'offline_minutes': {
      const last = Date.parse(snap.received_at);
      const ageMin = (Date.now() - last) / 60_000;
      const breached = ageMin > rule.threshold;
      return {
        breached,
        observed: ageMin,
        message: breached
          ? `Last seen ${ageMin.toFixed(0)}m ago · over ${rule.threshold}m threshold`
          : `Last seen ${ageMin.toFixed(0)}m ago · within ${rule.threshold}m`,
      };
    }
  }
}
